import { motion } from "framer-motion";
import Background, { Grain } from "@/components/Background";
import StitchDivider from "@/components/StitchDivider";
import Countdown from "@/components/Countdown";
import SignupForm from "@/components/SignupForm";
import Marquee from "@/components/Marquee";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollToForm = () => {
  if (window.__lenis) {
    window.__lenis.scrollTo("#private-list", { offset: -60, duration: 1.4 });
  } else {
    document.getElementById("private-list")?.scrollIntoView({ behavior: "smooth" });
  }
};

const Home = () => {
  return (
    <div className="relative min-h-screen">
      <Background />
      <Grain />

      <main>
        {/* ---------- HERO ---------- */}
        <section className="flex min-h-[100svh] items-center justify-center px-6 py-16 md:py-20">
          <div className="w-full max-w-[900px] text-center">
            <h1
              className="font-display text-[clamp(2.6rem,8vw,5.2rem)] leading-none tracking-[0.04em] text-[color:var(--matte-brown)]"
              style={{
                textShadow:
                  "0 1px 0 rgba(255,255,255,.05), 0 8px 30px rgba(0,0,0,.35)",
              }}
              data-testid="brand-wordmark"
            >
              <Line delay={0.15}>
                LACE{" "}
                <span className="font-serif-lux italic text-[color:var(--champagne)]">
                  &amp;
                </span>{" "}
                BOND
              </Line>
            </h1>

            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.7 }}
              className="my-5 md:my-6"
            >
              <StitchDivider />
            </motion.div>

            <p className="mb-10 md:mb-14 text-[0.62rem] sm:text-[0.78rem] tracking-[0.38em] uppercase text-[color:var(--muted)]" data-testid="brand-tagline">
              <Line delay={0.55}>Precision Meets Beauty.</Line>
            </p>

            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.9 }}
              className="mb-10 md:mb-14"
            >
              <Countdown />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 1.15 }}
            >
              <p className="font-serif-lux mb-2 text-[1rem] md:text-[1.1rem] tracking-[0.18em] uppercase" data-testid="coming-message">
                Something extraordinary is coming.
              </p>
              <p className="mb-8 md:mb-10 text-[0.72rem] tracking-[0.3em] uppercase text-[color:var(--champagne)]" data-testid="launch-date">
                April 19, 2027
              </p>

              <p className="mb-4 text-[0.68rem] tracking-[0.28em] uppercase text-[color:var(--muted)]" data-testid="signup-title">
                Be First to Experience Lace &amp; Bond.
              </p>

              <div id="private-list" className="scroll-mt-24">
                <SignupForm />
              </div>

              <p className="mt-2 text-[0.62rem] tracking-[0.04em] text-[#806e63]" data-testid="privacy-note">
                Join the private launch list for first access and updates. Unsubscribe anytime.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ---------- EDITORIAL MARQUEE ---------- */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.2, ease: EASE }}
        >
          <Marquee />
        </motion.div>

        {/* ---------- FIRST ACCESS ---------- */}
        <section className="px-6 py-24 md:py-36 text-center">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: EASE }}
            className="mx-auto max-w-[760px]"
          >
            <p className="mb-6 text-[0.62rem] tracking-[0.42em] uppercase text-[color:var(--bronze)]" data-testid="first-access-eyebrow">
              First Access
            </p>
            <h2 className="font-display text-[clamp(1.8rem,4.5vw,3.2rem)] leading-tight tracking-[0.02em]" data-testid="first-access-heading">
              Be first to experience{" "}
              <span className="font-serif-lux italic text-[color:var(--champagne)]">
                Lace &amp; Bond
              </span>
              .
            </h2>
            <p className="mx-auto mt-6 mb-10 max-w-[460px] text-[0.85rem] leading-relaxed tracking-[0.05em] text-[color:var(--muted)]" data-testid="first-access-subcopy">
              Join the private launch list for first access and updates.
            </p>
            <motion.button
              onClick={scrollToForm}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="rounded-full border border-[color:var(--champagne)]/60 px-10 py-4 text-[0.66rem] tracking-[0.28em] uppercase text-[color:var(--champagne)] transition-colors duration-300 hover:bg-[color:var(--champagne)] hover:text-[color:var(--espresso)]"
              data-testid="first-access-cta"
            >
              Join The List
            </motion.button>
          </motion.div>
        </section>

        {/* ---------- FOOTER ---------- */}
        <footer className="border-t border-[color:var(--champagne)]/10 px-6 py-10 text-center">
          <p className="font-display text-[0.85rem] tracking-[0.3em] text-[color:var(--matte-brown)]" data-testid="footer-brand">
            LACE{" "}
            <span className="font-serif-lux italic text-[color:var(--champagne)]">&amp;</span>{" "}
            BOND
          </p>
          <p className="mt-3 text-[0.58rem] tracking-[0.3em] uppercase text-[#806e63]" data-testid="footer-meta">
            04 · 19 · 2027 — Precision Meets Beauty
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Home;
